import { createRouter, createWebHistory } from "vue-router";
import { useSessionStore } from "@/stores/session-store";
import { useDebugStore } from "@/stores/debug-store";
// Navigation metadata is now defined via RouteMeta extension in @/types/routing-types
import AuditView from "../views/audit/AuditView.vue";
import LoginView from "../views/login/LoginView.vue";
import ForgotPasswordView from "../views/login/ForgotPasswordView.vue";
import BuilderView from "../views/builder/BuilderView.vue";
import HomeView from "../views/home/HomeView.vue";
import StyleGuideView from "../views/styleguide/StyleGuideView.vue";
// THROWAWAY prototype — delete with its route once the nested-run UI is designed.
import HealthDashboardView from "../views/sitehealth/HealthDashboardView.vue";
import NotFound404View from "../views/shared/NotFound404View.vue";
import SchedulerView from "../views/scheduler/SchedulerView.vue";
import TestingView from "../views/testing/TestingView.vue";
import GlobalRunHistoryView from "../views/runhistory/GlobalRunHistoryView.vue";
// Removed deleted scheduler view imports
import ProjectDashboard from "../views/projects/components/ProjectDashboard.vue";
import ProjectDetailView from "../views/projects/components/ProjectDetailView.vue";
import ProjectForm from "../views/projects/components/ProjectForm.vue";
import PublisherView from "../views/publisher/PublisherView.vue";
import ConnectionDashboard from "../views/connections/components/ConnectionDashboard.vue";
import CredentialsView from "../views/connections/components/CredentialsView.vue";
import CredentialDetailView from "../views/connections/components/CredentialDetailView.vue";
import ConnectionTemplatePicker from "../views/connections/components/ConnectionTemplatePicker.vue";
import ConnectionForm from "../views/connections/components/ConnectionForm.vue";
import ConnectionDetailView from "../views/connections/components/ConnectionDetailView.vue";
import ComponentsView from "../views/components/ComponentsView.vue";
import InternalServerError500 from "@/views/shared/InternalServerError500.vue";
import SettingsView from "../views/organisation/SettingsView.vue";
import OrgUsersView from "../views/organisation/OrgUsersView.vue";
import OrganisationView from "../views/organisation/OrganisationView.vue";
import UserProfileView from "../views/users/UserProfileView.vue";
import AdminActivityView from "../views/admin/AdminActivityView.vue";
import AdminOrganisationsView from "../views/admin/AdminOrganisationsView.vue";
import AdminOrgDetailView from "../views/admin/AdminOrgDetailView.vue";
import EmailTemplatesView from "../views/admin/EmailTemplatesView.vue";
import EmailTemplateDetailView from "../views/admin/EmailTemplateDetailView.vue";
import AdminProductsView from "../views/admin/AdminProductsView.vue";
import AdminConnectionTemplatesView from "../views/admin/AdminConnectionTemplatesView.vue";
import AdminConnectionTemplateDetailView from "../views/admin/AdminConnectionTemplateDetailView.vue";
import AdminNotifications from "../views/admin/AdminNotifications.vue";
import AdminDeadLettersView from "../views/admin/AdminDeadLettersView.vue";
import AdminEngineView from "../views/admin/AdminEngineView.vue";
import PartnersView from "../views/partners/PartnersView.vue";
import InviteAcceptView from "../views/invite/InviteAcceptView.vue";
import PartnershipAcceptView from "../views/partnerships/PartnershipAcceptView.vue";
import PartnersClientsView from "../views/partners/PartnersClientsView.vue";
import PartnerClientDetailView from "../views/partners/PartnerClientDetailView.vue";
import PartnerSettingsView from "../views/partners/PartnerSettingsView.vue";
import PartnerActivityView from "../views/partners/PartnerActivityView.vue";
import PartnerIssuesView from "../views/partners/PartnerIssuesView.vue";
import SubscriberDetailView from "../views/publisher/SubscriberDetailView.vue";

import BuilderForm from "@/views/builder/components/BuilderForm.vue";
export const router = createRouter({
  linkActiveClass: "active",
  history: createWebHistory(),
  routes: [
    {
      path: "/login",
      name: "login",
      component: LoginView,
      meta: { public: true },
    },
    {
      path: "/forgot-password",
      name: "forgot-password",
      component: ForgotPasswordView,
      meta: { public: true },
    },
    {
      path: "/invite/accept",
      name: "invite-accept",
      component: InviteAcceptView,
      props: { sideNav: false },
      meta: { public: true },
    },
    {
      path: "/partnerships/accept",
      name: "partnership-accept",
      component: PartnershipAcceptView,
      props: { sideNav: false },
      meta: { public: false }, // Requires authentication
    },
    {
      path: "/",
      name: "home",
      component: HomeView,
      meta: {
        sideNav: true,
        icon: "bi bi-speedometer2",
        title: "Dashboard",
        group: "main",
      },
    },
    {
      // Org-wide run history — every project's runs on one page. Sits second in the main nav (after
      // Dashboard, before Projects); the per-project History tab is the scoped version and is unchanged.
      // The deep-link children carry no projectid: the project a run belongs to is resolved from the run.
      path: "/runs",
      name: "global-runs",
      component: GlobalRunHistoryView,
      meta: {
        sideNav: true,
        icon: "bi bi-clock-history",
        title: "Run History",
        group: "main",
      },
    },
    {
      path: "/runs/:workflowid/:runid",
      name: "global-runs-run",
      component: GlobalRunHistoryView,
      meta: { sideNav: false },
    },
    {
      path: "/runs/:workflowid/:runid/:serviceid",
      name: "global-runs-service",
      component: GlobalRunHistoryView,
      meta: { sideNav: false },
    },
    {
      path: "/projects",
      name: "projects",
      component: ProjectDashboard,
      meta: {
        sideNav: true,
        icon: "bi bi-boxes",
        title: "Projects",
        group: "main",
        dynamicChildren: "projects",
      },
    },
    {
      // Top-level Testing hub — a general capability for every project type (not publisher-only).
      // Org-wide across all the org's projects; the per-project Testing tab is the scoped version.
      path: "/testing",
      name: "testing",
      component: TestingView,
      meta: {
        sideNav: true,
        icon: "bi bi-clipboard-check",
        title: "Testing",
        group: "main",
      },
    },
    {
      path: "/projects/new",
      name: "project-new",
      component: ProjectForm,
      meta: { sideNav: false },
    },
    {
      // Static segment, so it must precede the "/projects/:projectid" dynamic route below.
      path: "/projects/recycle-bin",
      name: "projects-recycle-bin",
      component: () => import("../views/projects/components/DeletedProjectsView.vue"),
      meta: { sideNav: false },
    },
    {

      path: "/projects/:projectid/edit",
      name: "project-edit",
      component: ProjectForm,
    },
    {
      path: "/projects/:projectid",
      component: ProjectDetailView,
      meta: { sideNav: false },
      redirect: { name: "project-overview" },
      children: [
        {
          path: "",
          name: "project-detail",
          redirect: { name: "project-overview" },
        },
        {
          path: "overview",
          name: "project-overview",
          component: ProjectDetailView,
        },
        {
          path: "workflows",
          name: "project-workflows",
          component: ProjectDetailView,
        },
        {
          path: "workflows/:workflowid",
          name: "project-workflow-detail",
          component: ProjectDetailView,
        },
        {
          path: "connections",
          name: "project-connections",
          component: ProjectDetailView,
        },
        {
          path: "datastores",
          name: "project-datastores",
          component: ProjectDetailView,
        },
        {
          path: "history",
          name: "project-history",
          component: ProjectDetailView,
        },
        {
          path: "history/:workflowid/:runid",
          name: "project-history-run",
          component: ProjectDetailView,
        },
        {
          path: "history/:workflowid/:runid/:serviceid",
          name: "project-history-service",
          component: ProjectDetailView,
        },
        {
          path: "schedules",
          name: "project-schedules",
          component: ProjectDetailView,
        },
        {
          path: "testing",
          name: "project-testing",
          component: ProjectDetailView,
        },
        {
          path: "wizard",
          name: "project-wizard",
          component: ProjectDetailView,
        },
        {
          path: "product",
          name: "project-product",
          component: ProjectDetailView,
        },
      
      ],
    },
   
    // {
    //     path: "/project/:projectid/:runid?",
    //     name: "project",
    //     component: ProjectView,
    //     props: {default: true, sideNav: true, icon: "bi bi-boxes", title: "Project", group: "main"}
    // },
    {
      path: "/datastores",
      name: "datastores",
      component: NotFound404View,
      meta: {
        sideNav: false,
        icon: "bi bi-database",
        title: "Data Stores",
        group: "main",
      },
    },
    {
      path: "/connections",
      name: "connections",
      component: ConnectionDashboard,
      meta: {
        sideNav: true,
        icon: "bi bi-link",
        title: "Connections",
        group: "main",
      },
    },
    {
      // Sidebar child of Connections. Declared before /:connectionid so "credentials" isn't
      // swallowed as a connection id.
      path: "/connections/credentials",
      name: "credentials",
      component: CredentialsView,
      meta: {
        sideNav: true,
        parentRoute: "connections",
        icon: "bi bi-shield-lock",
        title: "Credentials",
        group: "main",
      },
    },
    {
      path: "/connections/credentials/:credentialId",
      name: "credential-detail",
      component: CredentialDetailView,
      meta: { sideNav: false },
    },
    {
      // Step 1 of create: pick a template. Declared before /:connectionid so "new" isn't
      // swallowed as a connection id.
      path: "/connections/new",
      name: "connection-new",
      component: ConnectionTemplatePicker,
      meta: { sideNav: false },
    },
    {
      // Step 2 of create: the chosen template's setup form (method chooser if >1 auth method).
      path: "/connections/new/:templateId",
      name: "connection-create",
      component: ConnectionForm,
      meta: { sideNav: false },
    },
    {
      path: "/connections/:connectionid/edit",
      name: "connection-edit",
      component: ConnectionForm,
      meta: { sideNav: false },
    },
    {
      path: "/connections/:connectionid",
      name: "connection-detail",
      component: ConnectionDetailView,
      meta: { sideNav: false },
    },
    {
      path: "/components",
      name: "components",
      component: ComponentsView,
      meta: {
        sideNav: true,
        icon: "bi bi-box-seam",
        title: "Components",
        group: "main",
      },
    },
    {
      path: "/org/profile",
      name: "profile",
      component: UserProfileView,
      meta: {
        sideNav: true,
        parentRoute: "org",
        icon: "bi bi-person",
        title: "Profile",
        group: "main",
      },
    },
    {
      path: "/projects/:projectid/builder/new",
      name: "builder",
      component: BuilderForm,
      meta: { sideNav: false },
    },
    {
      path: "/projects/:projectid/builder/editor",
      redirect: to => ({
        name: "builder-project",
        params: { projectid: to.params.projectid },
      }),
    },
    {
      path: "/projects/:projectid/builder/:workflowid?/:view?/:runid?",
      name: "builder-project",
      component: BuilderView,
      meta: { sideNav: false },
    },
    {
      path: "/scheduler",
      name: "scheduler",
      component: SchedulerView,
      meta: {
        sideNav: true,
        icon: "bi bi-calendar",
        title: "Scheduler",
        group: "main",
      },
    },
    {
      path: "/scheduler/new",
      name: "scheduler-new",
      component: () => import("../views/scheduler/ScheduleEditView.vue"),
      meta: { sideNav: false },
    },
    {
      path: "/scheduler/edit/:scheduleId",
      name: "scheduler-edit",
      component: () => import("../views/scheduler/ScheduleEditView.vue"),
      meta: { sideNav: false },
    },
    {
      path: "/org",
      name: "org",
      component: OrganisationView,
      meta: {
        sideNav: true,
        icon: "bi bi-building",
        title: "Organisation",
        group: "main",
      },
    },
    {
      path: "/org/users",
      name: "org-users",
      component: OrgUsersView,
      meta: {
        sideNav: true,
        icon: "bi bi-people",
        title: "Users",
        group: "main",
        parentRoute: "org",
      },
    },
    {
      path: "/org/settings",
      name: "settings",
      component: SettingsView,
      meta: {
        sideNav: true,
        icon: "bi bi-gear",
        title: "Settings",
        group: "main",
        parentRoute: "org",
      },
    },
    {
      path: "/partners",
      name: "partners",
      component: PartnersView,
      meta: {
        sideNav: true,
        icon: "bi bi-people-fill",
        title: "Overview",
        group: "partners",
        // Sibling landing page (not a parent of /partners/clients etc.), so match exactly —
        // otherwise the startsWith prefix rule keeps Overview highlighted on every sub-page.
        // Mirrors the publisher "Overview" route.
        exact: true,
      },
    },
    {
      path: "/partners/clients",
      name: "partners-clients",
      component: PartnersClientsView,
      meta: {
        sideNav: true,
        icon: "bi bi-people",
        title: "Clients",
        group: "partners",
      },
    },
    {
      path: "/partners/clients/:id",
      name: "partners-client-detail",
      component: PartnerClientDetailView,
      meta: {
        group: "partners",
      },
    },
    {
      path: "/partners/activity",
      name: "partners-activity",
      component: PartnerActivityView,
      meta: {
        sideNav: true,
        icon: "bi bi-activity",
        title: "Activity",
        group: "partners",
      },
    },
    {
      path: "/partners/issues",
      name: "partners-issues",
      component: PartnerIssuesView,
      meta: {
        sideNav: true,
        icon: "bi bi-exclamation-triangle",
        title: "Issues",
        group: "partners",
      },
    },
    {
      path: "/partners/settings",
      name: "partners-settings",
      component: PartnerSettingsView,
      meta: {
        sideNav: true,
        icon: "bi bi-gear",
        title: "Settings",
        group: "partners",
      },
    },
    {
      path: "/audit",
      name: "audit",
      component: AuditView,
      meta: {
        sideNav: true,
        icon: "bi bi-clipboard-data",
        title: "Audit",
        group: "admin",
      },
    },
    {
      path: "/admin/activity",
      name: "admin-activity",
      component: AdminActivityView,
      meta: {
        sideNav: true,
        icon: "bi bi-activity",
        title: "Activity",
        group: "admin",
      },
    },
    {
      path: "/admin/organisations",
      name: "admin-organisations",
      component: AdminOrganisationsView,
      meta: {
        sideNav: true,
        icon: "bi bi-buildings",
        title: "Organisations",
        group: "admin",
      },
    },
    {
      path: "/admin/organisations/:id",
      name: "admin-org-detail",
      component: AdminOrgDetailView,
      meta: {
        sideNav: false,
        parentRoute: "admin-organisations",
        group: "admin"
      },
    },
    {
      path: "/admin/email-templates",
      name: "admin-email-templates",
      component: EmailTemplatesView,
      props: {
        sideNav: true,
        icon: "bi bi-envelope",
        title: "Email Templates",
        group: "admin",
      },
    },
    {
      path: "/admin/email-templates/:id",
      name: "admin-email-template-detail",
      component: EmailTemplateDetailView,
      props: {
        sideNav: false,
        parentRoute: "admin-email-templates",
        group: "admin"
      },
    },
    {
      path: "/admin/connection-templates",
      name: "admin-connection-templates",
      component: AdminConnectionTemplatesView,
      meta: {
        sideNav: true,
        icon: "bi bi-plug",
        title: "Connection Templates",
        group: "admin",
      },
    },
    {
      path: "/admin/connection-templates/:id",
      name: "admin-connection-template-detail",
      component: AdminConnectionTemplateDetailView,
      meta: {
        sideNav: false,
        parentRoute: "admin-connection-templates",
        group: "admin",
      },
    },
    {
      path: "/admin/products",
      name: "admin-products",
      component: AdminProductsView,
      props: {
        sideNav: true,
        icon: "bi bi-box-seam",
        title: "Products",
        group: "admin",
      },
    },
    {
      path: "/admin/dead-letters",
      name: "admin-dead-letters",
      component: AdminDeadLettersView,
      meta: {
        sideNav: true,
        icon: "bi bi-envelope-exclamation",
        title: "Dead letters",
        group: "admin",
      },
    },
    {
      path: "/admin/engine",
      name: "admin-engine",
      component: AdminEngineView,
      meta: {
        sideNav: true,
        icon: "bi bi-speedometer2",
        title: "Engine",
        group: "admin",
      },
    },
    {
      path: "/admin/notifications",
      name: "admin-notifications",
      component: AdminNotifications,
      props: {
        sideNav: true,
        icon: "bi bi-bell",
        title: "Notifications",
        group: "admin",
      },
    },
    {
      path: "/styleguide",
      name: "styleguide",
      component: StyleGuideView,
      redirect: { name: "styleguide-elements" },
      meta: {
        sideNav: true,
        icon: "bi bi-brush",
        title: "Style Guide",
        group: "dev",
      },
      children: [
        {
          path: "elements",
          name: "styleguide-elements",
          component: StyleGuideView,
        },
        {
          path: "components",
          name: "styleguide-components",
          component: StyleGuideView,
        },
        {
          path: "theme",
          name: "styleguide-theme",
          component: StyleGuideView,
        },
      ],
    },
    {
      path: "/sitehealth",
      name: "sitehealth",
      component: HealthDashboardView,
      meta: {
        sideNav: true,
        icon: "bi bi-clipboard-pulse",
        title: "Site Health",
        group: "dev",
      },
    },
    {
      path: "/internal-server-error",
      name: "internal-server-error",
      component: InternalServerError500,
      meta: { sideNav: false },
    },
    {
      path: "/forbidden",
      name: "forbidden",
      component: InternalServerError500,
      meta: { sideNav: false },
    },
    {
      path: "/:pathMatch(.*)",
      name: "not-found",
      component: NotFound404View,
      meta: { sideNav: false },
    },


    {
      path: "/publisher",
      name: "publisher-overview",
      component: PublisherView,
      meta: {
        sideNav: true,
        icon: "bi bi-house",
        title: "Overview",
        group: "publisher",
        exact: true,
      },
    },
    {
      path: "/publisher/products",
      name: "publisher-products",
      component: PublisherView,
      meta: {
        sideNav: true,
        icon: "bi bi-box-seam",
        title: "Products",
        group: "publisher",
        dynamicChildren: "products",
      },
    },
    {
      path: "/publisher/subscribers",
      name: "publisher-subscribers",
      component: PublisherView,
      meta: {
        sideNav: true,
    
        icon: "bi bi-people",
        title: "Subscribers",
        group: "publisher",
      },
    },
    {
      path: "/publisher/analytics",
      name: "publisher-analytics",
      component: PublisherView,
      meta: {
        sideNav: true,

        icon: "bi bi-graph-up",
        title: "Analytics",
        group: "publisher",
      },
    },

    // {
    //   path: "/publisher",
    //   name: "publisher",
    //   component: PublisherView,
    //   redirect: { name: "publisher-overview" },
    //   meta: {
    //     sideNav: true,
    //     icon: "bi bi-grid",
    //     title: "Publisher",
    //   }
    // },
    {
      path: "/publisher/:productId",
      name: "publisher-product-detail",
      component: PublisherView,
      meta: { sideNav: false },
    },
    {
      path: "/publisher/subscribers/:subscriptionId",
      name: "publisher-subscriber-detail",
      component: SubscriberDetailView,
      props: { sideNav: false },
    },
  ],
});

// Route guard to check org capabilities and internal-only access
router.beforeEach((to, from, next) => {
  // Latch session debug mode on when any navigation carries ?debug=1 — reveals developer-only menu
  // items (e.g. the run-history simulator) without shipping a visible toggle. Session-scoped; a reload
  // or new tab clears it.
  useDebugStore().activateFromQuery(to);

  const sessionStore = useSessionStore();
  const { canPublishProducts, canPartnerWithOrgs, isSuperAdmin, role } = sessionStore;

  if (to.path.startsWith('/publisher')) {
    if (!canPublishProducts) {
      console.warn('Access denied: Organization does not have publisher capability');
      next({ name: 'home' });
      return;
    }
  }

  if (to.path.startsWith('/partners')) {
    if (!canPartnerWithOrgs) {
      console.warn('Access denied: Organization does not have partnership capability');
      next({ name: 'home' });
      return;
    }
  }

  // Admin and Dev sections are internal-only — SuperAdmin AND a real Membership in the current
  // org (any role). Mirrors useNavigation's filterByCapabilities so direct URL nav can't bypass
  // the hidden nav. A SuperAdmin viewing a foreign org carries the synthetic SuperAdminView
  // role and is correctly denied here.
  const isInternalRoute =
    to.path.startsWith('/admin') ||
    to.path === '/audit' ||
    to.path.startsWith('/styleguide') ||
    to.path.startsWith('/sitehealth');
  if (isInternalRoute && !(isSuperAdmin && role !== 'SuperAdminView')) {
    console.warn('Access denied: internal-only section');
    next({ name: 'home' });
    return;
  }

  next();
});

export default router;
